import { useEffect, useState } from "react";

/** Keeps scroll position per list page so returning from a detail view lands where the user left. */
export function useScrollRestore(key, ready) {
  useEffect(() => {
    if (!ready) return;
    const y = Number(sessionStorage.getItem(key) || 0);
    if (y > 0) {
      const id = window.setTimeout(() => window.scrollTo(0, y), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [ready, key]);
  useEffect(() => {
    const onScroll = () => sessionStorage.setItem(key, String(window.scrollY));
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [key]);
}

/** Scroll to top when the route changes – list pages override this with useScrollRestore. */
export function useScrollToTop(pathname) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
}

/** True when the media query matches (SSR-safe default false). */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => (typeof window !== "undefined" ? window.matchMedia(query).matches : false));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

/** Desktop breakpoint used for the inline right panel. */
export const useIsDesktop = () => useMediaQuery("(min-width: 1024px)");

/** Width of an element, kept up to date with a ResizeObserver. */
export function useElementWidth(ref) {
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setW(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setW(Math.round(e.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}
